import { useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Badge } from '../../components/Badge.tsx';
import { Button } from '../../components/Button.tsx';
import { Checkbox } from '../../components/Checkbox.tsx';
import { useOwner } from './controller.ts';
import { DomainPublication } from './DomainPublication.tsx';
import { DomainScopeCards } from './DomainScopeCards.tsx';
import { date, download, TESTING_LEVEL_NAMES, type TestingLevel } from './model.ts';
import { PublicLayout } from './PublicLayout.tsx';
import styles from './authorization.module.css';

const OWNER_STATUS: Record<
  string,
  { label: string; tone: 'positive' | 'neutral' | 'attention' | 'negative'; shape: string }
> = {
  active: { label: 'Active', tone: 'positive', shape: '✓' },
  scheduled: { label: 'Scheduled', tone: 'neutral', shape: '○' },
  suspended: { label: 'Suspended · DNS proof missing', tone: 'attention', shape: '△' },
  unavailable: { label: 'DNS proof stale', tone: 'attention', shape: '△' },
  expired: { label: 'Ended', tone: 'neutral', shape: '×' },
  revoked: { label: 'Revoked', tone: 'negative', shape: '×' },
};

// Owner management for one letter, inside the app. The public view lives at /verify?domain=.
export function OwnerPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const state = useOwner(id);
  const [revoking, setRevoking] = useState(false);
  const [revokeAccepted, setRevokeAccepted] = useState(false);
  const letter = state.letter;
  const status = letter
    ? (OWNER_STATUS[letter.status] ?? {
        label: letter.status,
        tone: 'neutral' as const,
        shape: '○',
      })
    : null;
  return (
    <PublicLayout title="Manage your authorization">
      {state.error && (
        <p role="alert" className={styles.error}>
          {state.error}
        </p>
      )}
      {state.notice && (
        <p role="status" className={styles.notice}>
          {state.notice}
        </p>
      )}
      {letter && status ? (
        <article className={styles.panel} aria-labelledby="owner-title">
          <div className={styles.header}>
            <div className={styles.intro}>
              <p className={styles.kicker}>Owner controls · {letter.terms.engagement}</p>
              <h2 id="owner-title">{letter.terms.domain}</h2>
            </div>
            <Badge tone={status.tone} shape={status.shape} data-badge={letter.status}>
              {status.label}
            </Badge>
          </div>
          <dl className={styles.facts}>
            <div>
              <dt>Testing level</dt>
              <dd>
                <strong>
                  {TESTING_LEVEL_NAMES[letter.terms.testingLevel as TestingLevel] ??
                    letter.terms.testingLevel}
                </strong>
              </dd>
            </div>
            <div>
              <dt>Testing window · your local time</dt>
              <dd>
                <strong>
                  {date(letter.terms.startsAt)} → {date(letter.terms.endsAt)}
                </strong>
              </dd>
            </div>
            <div>
              <dt>Scope</dt>
              <dd>
                <DomainScopeCards domains={letter.terms.domains} domain={letter.terms.domain} />
                <span>
                  {letter.terms.exclusions.length
                    ? `Excludes ${letter.terms.exclusions.join(', ')}`
                    : 'No exclusions'}
                </span>
              </dd>
            </div>
          </dl>
          <div className={styles.actions}>
            <Button
              variant="primary"
              onPress={async () => {
                const w = await state.enterWorkspace();
                if (w) void navigate({ to: '/', replace: true });
              }}
              isDisabled={state.busy}
            >
              Enter workspace
            </Button>
            <Button
              onPress={() =>
                download(
                  { letter: letter.terms, termsHash: letter.termsHash, jws: letter.privateJws },
                  `traceira-private-${id}.json`,
                )
              }
            >
              Download private signed letter
            </Button>
            {letter.status !== 'revoked' && (
              <Button variant="tertiary" onPress={() => setRevoking(true)}>
                Revoke authorization
              </Button>
            )}
          </div>
          {revoking && (
            <div className={styles.notice} role="group" aria-labelledby="revoke-title">
              <h3 id="revoke-title">Revoke this authorization?</h3>
              <p>
                Traceira stops at once and the letter cannot be reactivated; a new testing window
                needs a new letter and approval. Also delete the TXT record from your DNS so outside
                verifiers stop seeing it.
              </p>
              <Checkbox isSelected={revokeAccepted} onChange={setRevokeAccepted}>
                End this authorization and its exclusive provider policy now.
              </Checkbox>
              <div className={styles.actions}>
                <Button
                  onPress={() => {
                    void state.revoke().then(() => setRevoking(false));
                  }}
                  isDisabled={!revokeAccepted || state.busy}
                >
                  Confirm revocation
                </Button>
                <Button variant="tertiary" onPress={() => setRevoking(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {!letter.revokedAt && <DomainPublication letter={letter} />}
        </article>
      ) : (
        <section className={styles.panel} aria-labelledby="owner-access">
          <h2 id="owner-access">Owner access needed</h2>
          <p>
            Only the recorded authorizer can download the private letter, publish it or revoke it.
            Send a management link to their mailbox.
          </p>
          <div className={styles.actions}>
            <Button onPress={() => void state.managementEmail()} isDisabled={state.busy}>
              Email the authorizer a management link
            </Button>
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
